import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { MediaView } from '../shared/MediaView';
import { founder, salon } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { useCatalog } from '../../contexts/CatalogContext';
import type { ArticleCategory } from '../../core/types/content';
import { plural } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { chipCls } from '../../ui/styles';
import { heroTitle, quietLink } from '../../ui/sections';
import { MentionedProducts } from './MentionedProducts';
import { JournalPodcast } from './JournalPodcast';
import { articlePath, articlesIn, categoriesOf } from './journalRules';

/**
 * Het journal als tijdschrift: kop, het nieuwste artikel groot met beeld en genoemde producten,
 * de rest in een raster (beeld waar dat er is), en de podcast op klei.
 */
export function Journal() {
  const pages = usePages();
  const {
    articles,
    productsByIds
  } = useCatalog();
  const copy = pages.journal;
  const categories = categoriesOf(articles);
  // De categorie staat in het adres (?categorie=…): terug uit een artikel staat dezelfde keuze er nog.
  const [params, setParams] = useSearchParams();
  const asked = params.get('categorie');
  const category: ArticleCategory | null = categories.find(c => c === asked) ?? null;
  const setCategory = (c: ArticleCategory | null) => {
    const next = new URLSearchParams(params);
    if (c) next.set('categorie', c);else next.delete('categorie');
    setParams(next, {
      replace: true,
      preventScrollReset: true
    });
  };
  const linkState = {
    fromJournal: `/journal${params.toString() ? `?${params.toString()}` : ''}`
  };
  const [featured, ...rest] = articlesIn(articles, category);
  const featuredProducts = productsByIds(featured?.productIds ?? []);
  return <>
      <section className="bg-sand" data-id="element-7582" data-mp="2yuhob9o2nzkl">
        <div className="mx-auto grid max-w-content gap-10 px-6 pb-10 pt-12 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:pb-14 lg:pt-20" data-id="element-7583" data-mp="2ytga4wbrewyn">
          <div className="lg:col-span-8" data-id="element-7584" data-mp="36x0bmojsotjq">
            {copy.hero.eyebrow && <p className="text-[14px] text-clay" data-id="element-7585" data-mp="3auob84wfnahy">{fill(copy.hero.eyebrow)}</p>}
            <h1 className={`mt-4 max-w-3xl lg:mt-5 ${heroTitle}`} data-id="element-7586" data-mp="3fwdpdteyc4a9">
              <HeadingText heading={copy.hero.heading} data-id="element-7587" data-mp="3ku6tmgwp2o7w" />
            </h1>
          </div>
          <div className="lg:col-span-4 lg:self-end" data-id="element-7588" data-mp="3ks2pbxuvvn73">
            <div className="flex items-center gap-4 border-t border-ink/15 pt-6" data-id="element-7589" data-mp="2n7znv3onmpty">
              {/* Decoratief: haar naam staat ernaast. */}
              <img src={founder.portrait} alt="" width={56} height={56} className="h-14 w-14 shrink-0 rounded-full object-cover object-top" data-id="element-7590" data-mp="2s3f4g3e1qzu2" />
              <p className="text-[14px] leading-snug text-muted" data-id="element-7591" data-mp="31wmfo4vgxa22">
                {fill(copy.hero.byline)} <span className="text-ink" data-id="element-7592" data-mp="24gyrmnex58e3">{founder.name}</span>
                <br data-id="element-7593" data-mp="24eciwkgakmua" />
                {founder.role} · {salon.city}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Artikelen: eerst, want hier komen lezers via Google binnen */}
      <section className="bg-sand pb-20 sm:pb-24 lg:pb-32" aria-labelledby="journal-articles" data-id="element-7594" data-mp="1wwxpdr479q2g">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7595" data-mp="1rxba7c4t9zr0">
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6" data-id="element-7596" data-mp="1rzo14esjrkg7">
            <h2 id="journal-articles" className="text-[14px] text-ink" data-id="element-7597" data-mp="1n0kxmys7tmac">
              {fill(copy.articlesHeading)}
            </h2>
            <div role="group" aria-label="Kies een categorie" className="-mx-6 flex min-w-0 max-w-[calc(100%+3rem)] gap-2 overflow-x-auto px-6 [scrollbar-width:none] sm:mx-0 sm:max-w-full sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7598" data-mp="1e5myvb2dzfb3">
              <button type="button" aria-pressed={category === null} onClick={() => setCategory(null)} className={`${chipCls(category === null)} shrink-0`} data-id="element-7599" data-mp="z8nsfqj9tsi7">
                Alles
              </button>
              {categories.map(c => <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`${chipCls(category === c)} shrink-0`} data-id="element-7600" data-mp="1jyi20oe8jij1">
                  {c}
                </button>)}
            </div>
          </div>

          {featured ? <article className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-16" aria-labelledby="featured-title" data-id="element-7601" data-mp="1f66qeoa4u8an">
              <Link to={articlePath(featured)} state={linkState} className="block lg:col-span-7" tabIndex={-1} aria-hidden="true" data-id="element-7602" data-mp="116p9ev32vzbu">
                {/* Sfeerbeeld: de titel staat ernaast. Zonder eigen beeld het portret, zodat het vlak nooit leeg is. */}
                <MediaView media={{
              image: featured.image || founder.portraitPalm,
              alt: ''
            }} priority sizes="(min-width: 1024px) 55vw, 100vw" className="aspect-[4/3] w-full" data-id="element-7603" data-mp="w845akkpgvi4" />
              </Link>
              <div className="lg:col-span-5 lg:self-center" data-id="element-7604" data-mp="2qm53r4ccfgho">
                <p className="text-[13px] text-clay" data-id="element-7605" data-mp="2qllwqio8ecif">
                  {featured.category} · {featured.readTime} · {featured.date}
                </p>
                <h3 id="featured-title" className="mt-3 font-display text-[2.2rem] font-light leading-[1.08] text-ink sm:text-[2.9rem]" data-id="element-7606" data-mp="axztnxw7hdyg">
                  <Link to={articlePath(featured)} state={linkState} className="transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7607" data-mp="oqfpadms29pc">
                    {featured.title}
                  </Link>
                </h3>
                <p className="mt-5 text-[17px] leading-relaxed text-muted" data-id="element-7608" data-mp="yp59we62689a">{featured.excerpt}</p>
                <Link to={articlePath(featured)} state={linkState} className={`group mt-7 ${quietLink} text-ink`} data-id="element-7609" data-mp="tq23apafjtz7">
                  Lees het artikel<span className="sr-only" data-id="element-7610" data-mp="12mjl2bdauq3x">: {featured.title}</span>
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7611" data-mp="39fvgvattm2g1" />
                </Link>

                {featuredProducts.length > 0 && <div className="mt-9 border-t border-ink/15 pt-6" data-id="element-7612" data-mp="1dbxoxbychswb">
                    <p className="text-[14px] text-ink" data-id="element-7613" data-mp="1dc7gv52yaa4n">Genoemd in dit artikel</p>
                    <MentionedProducts products={featuredProducts} data-id="element-7614" data-mp="1ia90br4agbgn" />
                  </div>}
              </div>
            </article> : <p className="mt-10 text-[16px] text-muted" data-id="element-7615" data-mp="1rc8gyibe5dj2">{fill(copy.emptyCategory)}</p>}

          {rest.length > 0 && <ul className="mt-20 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3" data-id="element-7616" data-mp="1w9iekl8xovhz">
              {rest.map(a => <li key={a.slug} className="h-full" data-id="element-7617" data-mp="212bgz2gxjrqw">
                  <Link to={articlePath(a)} state={linkState} className="group flex h-full flex-col" data-id="element-7618" data-mp="260w7ubaakz2o">
                    {a.image ? <MediaView media={{
                image: a.image,
                alt: ''
              }} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" className="aspect-[3/2] w-full" data-id="element-7619" data-mp="2a419ycqgmyqs" /> :
              // Geen beeld: een rustig vlak in dezelfde maat, zodat titels in de rij gelijk staan.
              <span aria-hidden="true" className="flex aspect-[3/2] w-full items-end bg-shell p-5" data-id="element-7620" data-mp="3uixqqx3akfl1">
                        <span className="font-display text-[1.2rem] italic text-clay" data-id="element-7621" data-mp="3qkrnhz1nwi4v">{a.category}</span>
                      </span>}
                    <p className="mt-5 text-[13px] text-clay" data-id="element-7622" data-mp="m4p6qdeu4x4h">{a.category}</p>
                    <h3 className="mt-2 font-display text-[1.6rem] font-light leading-snug text-ink transition-colors duration-150 ease-soft group-hover:text-clay" data-id="element-7623" data-mp="i33rwmiqq8rn">
                      {a.title}
                    </h3>
                    <p className="mt-3 text-[15px] leading-relaxed text-muted" data-id="element-7624" data-mp="8bzgtxw6r823">{a.excerpt}</p>
                    <p className="mt-auto pt-4 text-[13px] text-muted" data-id="element-7625" data-mp="15soctaygfg3k">
                      {a.date} · {a.readTime}
                      {a.productIds && a.productIds.length > 0 && <span className="text-clay" data-id="element-7626" data-mp="9e50n1hzdp9q"> · {plural(a.productIds.length, 'product', 'producten')} uit de shop</span>}
                    </p>
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <JournalPodcast data-id="element-7627" data-mp="3vmxwnic3guyc" />
    </>;
}